import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import GroupsIcon from "@mui/icons-material/Groups";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";
import ReferralDetailsModal from "../models/ReferralDetailsModal ";
import truncateWithEllipsis from "../../utils/common/truncateWithEllipsis ";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import InfluencerCommissionModal from "../models/InfluencerCommissionModal";
import { axiosConfig } from "../../axios/axiosConfig";

const headCellSx = {
  fontWeight: 700,
  fontSize: "11px",
  color: "#fff",
  background: "#04343a",
  borderBottom: "none",
  padding: "10px 12px",
  whiteSpace: "nowrap",
};

const UserReferral = () => {
  const [searchMobile, setSearchMobile] = useState("");
  const [searchedUser, setSearchedUser] = useState(null);
  const [adminComment, setAdminComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [influencerList, setInfluencerList] = useState([]);
  const [influencerListLoading, setInfluencerListLoading] = useState(false);
  const [, setEditRowId] = useState(null);
  const [editComment, setEditComment] = useState("");
  const [editStatus, setEditStatus] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [referralModalOpen, setReferralModalOpen] = useState(false);
  const [referralUsers, setReferralUsers] = useState([]);
  const [modalTitle, setModalTitle] = useState("");
  const [showConversionDate, setShowConversionDate] = useState(false);
  const [commissionModalOpen, setCommissionModalOpen] = useState(false);
  const [selectedInfluencerId, setSelectedInfluencerId] = useState("");
  const [influencerSearch, setInfluencerSearch] = useState("");
  const [filteredInfluencerList, setFilteredInfluencerList] = useState([]);

  const handleSearch = async () => {
    if (!/^\d{10}$/.test(searchMobile)) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return;
    }
    setLoading(true);
    setSearchedUser(null);
    try {
      const response = await axiosConfig({
        method: "post",
        url: "/tnibroadmin/search-user-by-mobile/",
        data: { mobile_number: searchMobile },
      });
      const data = response?.data?.response;
      if (data.username && data.phoneno) {
        setSearchedUser({
          id: data?.id,
          username: data?.username,
          email: data.email || "Not provided",
          phoneno: data.phoneno,
          date_joined: data.date_joined,
          referralcode: data.referralcode,
        });
        toast.success("User found successfully");
      } else {
        toast.error("User data is incomplete or not found.");
      }
    } catch (error) {
      toast.error(error.response.data.response || error.message || "Error fetching user data.");
    } finally {
      setLoading(false);
    }
  };

  const fetchInfluencerList = async () => {
    setInfluencerListLoading(true);
    try {
      const response = await axiosConfig({ method: "get", url: "/tnibroadmin/influencer-list-api/" });
      const data = response?.data?.response;
      setInfluencerList(data);
      setFilteredInfluencerList(data);
    } catch (error) {
      toast.error(error?.message || error?.response?.data);
    } finally {
      setInfluencerListLoading(false);
    }
  };

  useEffect(() => { fetchInfluencerList(); }, []);

  const handleSubmitInfluencer = async () => {
    if (!adminComment.trim()) { toast.error("Please enter a comment before submitting."); return; }
    try {
      const response = await axiosConfig({
        method: "post",
        url: "/tnibroadmin/influencer-create-api/",
        data: { user_id: searchedUser?.id.toString(), comment: adminComment.trim() },
      });
      toast.success(response?.data?.response);
      fetchInfluencerList();
      setDialogOpen(false);
      setAdminComment("");
    } catch (error) {
      toast.error(error.response.data.response || error.message);
    }
  };

  const allAmountKeys = Array.from(
    new Set(influencerList.flatMap((inf) => Object.keys(inf.user_count_by_amount || {})))
  ).sort((a, b) => Number(a) - Number(b));

  const handleUpdateInfluencer = async (id) => {
    try {
      await axiosConfig({
        method: "put",
        url: `/tnibroadmin/influencer-update-api/${id}/`,
        data: { comment: editComment, is_active: editStatus },
      });
      toast.success("Influencer updated successfully");
      fetchInfluencerList();
    } catch {
      toast.error("Failed to update influencer");
    }
  };

  const handleReferralClick = async (referralCode, filterBy) => {
    try {
      let title = "";
      if (filterBy === "totalreferrals") { title = "Total Referrals"; setShowConversionDate(false); }
      else if (filterBy === "totalconversions") { title = "Total Conversions"; setShowConversionDate(true); }
      else { title = `Plan ₹${filterBy}`; setShowConversionDate(false); }
      setModalTitle(title);
      const response = await axiosConfig({
        method: "get",
        url: `/tnibroadmin/user-details-for-referral/`,
        params: { referralcode: referralCode, filter_by: filterBy },
      });
      setReferralUsers(response?.data?.response || []);
      setReferralModalOpen(true);
    } catch (error) {
      toast.error(error?.response?.data?.response || error?.message || "Failed to fetch referral details");
    }
  };

  const dialogTitleSx = {
    background: "#04343a",
    color: "#fff",
    fontWeight: 700,
    fontSize: "16px",
    py: "14px",
    px: "20px",
  };

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Page Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
          p: "20px 28px",
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <GroupsIcon sx={{ color: "#fff", fontSize: 32 }} />
        <Box>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "20px" }}>
            Influencer Management
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "13px" }}>
            Search users and manage influencers
          </Typography>
        </Box>
      </Box>

      {/* Search User Card */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          overflow: "hidden",
          mb: 3,
        }}
      >
        <Box sx={{ background: "#04343a", px: "20px", py: "12px" }}>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "15px" }}>Search User</Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Enter Mobile Number"
                value={searchMobile}
                onChange={(e) => setSearchMobile(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>
            <Grid item>
              <Button
                startIcon={<SearchIcon />}
                disabled={loading}
                onClick={handleSearch}
                sx={{
                  background: "linear-gradient(135deg, #016551, #014d3d)",
                  color: "#fff",
                  fontWeight: 600,
                  borderRadius: "8px",
                  px: 3,
                  py: "7px",
                  boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
                  "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
                  "&.Mui-disabled": { background: "#e2e8f0", color: "#9ca3af" },
                }}
              >
                {loading ? "Searching..." : "Search"}
              </Button>
            </Grid>
          </Grid>

          {/* Search result inline */}
          {searchedUser && (
            <TableContainer component={Paper} elevation={0} sx={{ mt: 3, border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
              <Table size="small"
                sx={{
                  "& .MuiTableCell-root": { padding: "9px 14px", fontSize: "12px", borderBottom: "1px solid #f0f4f8" },
                  "& .MuiTableBody-root .MuiTableRow-root:hover": { background: "#f0faf5" },
                }}
              >
                <TableHead>
                  <TableRow>
                    {["Name", "Email", "Phone No", "Joined Date", "Referral Code", "Actions"].map((h) => (
                      <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ color: "#1e293b", fontWeight: 600 }}>{searchedUser.username}</TableCell>
                    <TableCell sx={{ color: "#64748b" }}>{searchedUser.email}</TableCell>
                    <TableCell sx={{ color: "#64748b" }}>{searchedUser.phoneno}</TableCell>
                    <TableCell sx={{ color: "#64748b" }}>{searchedUser.date_joined}</TableCell>
                    <TableCell sx={{ color: "#64748b" }}>{searchedUser.referralcode}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        startIcon={<PersonAddIcon />}
                        onClick={() => setDialogOpen(true)}
                        sx={{
                          background: "linear-gradient(135deg, #016551, #014d3d)",
                          color: "#fff",
                          fontWeight: 600,
                          borderRadius: "7px",
                          fontSize: "12px",
                          px: 2,
                          "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
                        }}
                      >
                        Make Influencer
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>

      {/* Influencer List Card */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            background: "#04343a",
            px: "20px",
            py: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "15px" }}>
              Influencer List
            </Typography>
            <Chip
              label={`Total: ${filteredInfluencerList.length}`}
              size="small"
              sx={{ bgcolor: "rgba(1,101,81,0.3)", color: "#fff", fontWeight: 600, fontSize: "12px" }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              size="small"
              placeholder="Name / Mobile / Referral"
              value={influencerSearch}
              onChange={(e) => setInfluencerSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const query = influencerSearch.toLowerCase().trim();
                  setFilteredInfluencerList(influencerList.filter(
                    (inf) =>
                      inf.influencer_username?.toLowerCase().includes(query) ||
                      inf.influencer_mobile_no?.toString().includes(query) ||
                      inf.referralcode?.toLowerCase().includes(query)
                  ));
                }
              }}
              sx={{
                width: 220,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px", fontSize: "13px",
                  bgcolor: "rgba(255,255,255,0.1)",
                  "& fieldset": { borderColor: "rgba(255,255,255,0.25)" },
                  "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" },
                  "&.Mui-focused fieldset": { borderColor: "#fff" },
                },
                "& .MuiOutlinedInput-input": { color: "#fff" },
                "& .MuiOutlinedInput-input::placeholder": { color: "rgba(255,255,255,0.5)" },
              }}
            />
            <Button
              size="small"
              startIcon={<SearchIcon />}
              onClick={() => {
                const query = influencerSearch.toLowerCase().trim();
                setFilteredInfluencerList(influencerList.filter(
                  (inf) =>
                    inf.influencer_username?.toLowerCase().includes(query) ||
                    inf.influencer_mobile_no?.toString().includes(query) ||
                    inf.referralcode?.toLowerCase().includes(query)
                ));
              }}
              sx={{
                background: "linear-gradient(135deg, #016551, #014d3d)",
                color: "#fff",
                fontWeight: 600,
                borderRadius: "7px",
                px: 2,
                fontSize: "12px",
                "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
              }}
            >
              Search
            </Button>
          </Box>
        </Box>

        {influencerListLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress sx={{ color: "#016551" }} />
          </Box>
        ) : filteredInfluencerList.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ color: "#94a3b8", fontSize: "13px" }}>No influencers found.</Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ borderTop: "1px solid #eef2f7", overflowX: "auto" }}>
            <Table
              size="small"
              sx={{
                minWidth: 1000,
                "& .MuiTableCell-root": {
                  padding: "9px 12px",
                  fontSize: "12px",
                  borderBottom: "1px solid #f0f4f8",
                  whiteSpace: "nowrap",
                },
                "& .MuiTableBody-root .MuiTableRow-root:hover": { background: "#f0faf5" },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, width: 45 }}>S.No.</TableCell>
                  <TableCell sx={{ ...headCellSx, minWidth: 130 }}>Influencer Name</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 110 }}>Mobile</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 100 }}>Joined</TableCell>
                  <TableCell sx={{ ...headCellSx, minWidth: 140 }}>Comment</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 100 }}>Created By</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 100 }}>Created Date</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 80 }}>Status</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 100 }}>Total Referrals</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 110 }}>Total Conversions</TableCell>
                  {allAmountKeys.map((amt) => (
                    <TableCell key={amt} sx={{ ...headCellSx, width: 80 }}>₹{amt}</TableCell>
                  ))}
                  <TableCell sx={{ ...headCellSx, width: 80 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredInfluencerList.map((inf, index) => (
                  <TableRow key={inf.influencer_user_id}>
                    <TableCell sx={{ color: "#94a3b8" }}>{index + 1}</TableCell>
                    <TableCell sx={{ color: "#1e293b", fontWeight: 600 }}>{inf.influencer_username}</TableCell>
                    <TableCell sx={{ color: "#64748b" }}>{inf.influencer_mobile_no}</TableCell>
                    <TableCell sx={{ color: "#64748b" }}>
                      {new Date(inf.date_of_joined).toLocaleDateString("en-IN")}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", color: "#64748b" }}>
                      <Tooltip title={inf.comment || ""} placement="top" arrow>
                        <span>{truncateWithEllipsis(inf.comment)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ color: "#64748b" }}>{inf.created_by}</TableCell>
                    <TableCell sx={{ color: "#64748b" }}>
                      {new Date(inf.created_at).toLocaleDateString("en-IN")}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={inf.is_active ? "Active" : "Inactive"}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: "11px",
                          bgcolor: inf.is_active ? "#dcfce7" : "#fee2e2",
                          color: inf.is_active ? "#15803d" : "#b91c1c",
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{ color: "#016551", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => handleReferralClick(inf.referralcode, "totalreferrals")}
                    >
                      {inf.totalreferrals}
                    </TableCell>
                    <TableCell
                      sx={{ color: "#016551", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => handleReferralClick(inf.referralcode, "totalconversions")}
                    >
                      {inf.totalconversions}
                    </TableCell>
                    {allAmountKeys.map((amt) => (
                      <TableCell
                        key={amt}
                        sx={{ color: "#016551", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => handleReferralClick(inf.referralcode, amt)}
                      >
                        {inf.user_count_by_amount[amt] || 0}
                      </TableCell>
                    ))}
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Edit" arrow>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditRowId(inf.influencer_user_id);
                              setEditComment(inf.comment);
                              setEditStatus(inf.is_active);
                              setEditData(inf);
                              setEditDialogOpen(true);
                            }}
                            sx={{ color: "#04343a", "&:hover": { bgcolor: "#e6f4f1" } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Influencer Commission" arrow>
                          <IconButton
                            size="small"
                            onClick={() => { setSelectedInfluencerId(inf?.id); setCommissionModalOpen(true); }}
                            sx={{ color: "#016551", "&:hover": { bgcolor: "#e6f4f1" } }}
                          >
                            <CurrencyRupeeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Confirm Influencer Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={dialogTitleSx}>
          Confirm Influencer
          <IconButton onClick={() => setDialogOpen(false)} sx={{ position: "absolute", right: 8, top: 8, color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff" } }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: "16px !important" }}>
          <TextField value={searchedUser?.username} label="Username" InputProps={{ readOnly: true }} fullWidth />
          <TextField
            fullWidth multiline minRows={2} label="Admin Comment"
            value={adminComment} variant="outlined"
            onChange={(e) => setAdminComment(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: "#b91c1c", fontWeight: 600 }}>Cancel</Button>
          <Button
            onClick={handleSubmitInfluencer}
            sx={{
              background: "linear-gradient(135deg, #016551, #014d3d)",
              color: "#fff", fontWeight: 600, borderRadius: "8px", px: 3,
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Influencer Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={dialogTitleSx}>
          Edit Influencer
          <IconButton onClick={() => setEditDialogOpen(false)} sx={{ position: "absolute", right: 8, top: 8, color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff" } }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ overflowX: "auto", "&::-webkit-scrollbar": { display: "none" }, scrollbarWidth: "none", pt: "16px !important" }}>
          {editData && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth label="Username" value={editData.influencer_username} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Mobile" value={editData.influencer_mobile_no} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Joined Date" value={new Date(editData.date_of_joined).toLocaleDateString("en-IN")} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Created By" value={editData.created_by} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline label="Edit Comment" value={editComment} onChange={(e) => setEditComment(e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField select fullWidth label="Status" value={editStatus ? "active" : "inactive"} onChange={(e) => setEditStatus(e.target.value === "active")}>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ color: "#b91c1c", fontWeight: 600 }}>Cancel</Button>
          <Button
            onClick={async () => { await handleUpdateInfluencer(editData.influencer_user_id); setEditDialogOpen(false); }}
            sx={{
              background: "linear-gradient(135deg, #016551, #014d3d)",
              color: "#fff", fontWeight: 600, borderRadius: "8px", px: 3,
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <ReferralDetailsModal
        open={referralModalOpen}
        onClose={() => setReferralModalOpen(false)}
        title={modalTitle}
        referralUsers={referralUsers}
        showConversionDate={showConversionDate}
      />
      <InfluencerCommissionModal
        open={commissionModalOpen}
        onClose={() => setCommissionModalOpen(false)}
        influencerUserId={selectedInfluencerId}
      />
    </Box>
  );
};

export default UserReferral;
